import { useMemo } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { TextField } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  META_PHASE_MIN_GAP_MINUTES,
  formatPickerDayjsToEventWallMinute,
  snapPickerDayjsToWallMinute,
  snapWallMinuteToMetaBudgetUtc,
  wallMinuteToDayjsInEventZone,
} from "../../utils/eventWallTime";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Phased budget pickers sit in the create-flow left column (`overflow: auto`). Use `fixed`
 * strategy, a high z-index (Chakra layers / scroll stacking), and a relaxed focus trap so the
 * desktop popper opens reliably and stays usable.
 */
const pickerPopperSlotProps = {
  strategy: "fixed" as const,
  sx: { zIndex: 10000 },
};

const pickerDesktopTrapFocusSlotProps = {
  disableEnforceFocus: true,
} as const;

/** e.g. Mar 30 12:00 AM (no year in picker). */
const PHASE_DATETIME_FIELD_FORMAT = "MMM D h:mm A";

/** MUI fields sit inside one outer shell: no inner box border. */
const pickerInRangeFieldSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: "14px",
    fontFamily: "inherit",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    minHeight: "40px",
    alignItems: "center",
    backgroundColor: "transparent",
    "& fieldset": {
      borderWidth: 0,
      borderColor: "transparent",
      borderRadius: 0,
      boxShadow: "none",
    },
    "&:hover fieldset": { borderColor: "transparent" },
    "&.Mui-focused fieldset": {
      borderWidth: 0,
      borderColor: "transparent",
      boxShadow: "none",
    },
    "& .MuiInputBase-input": {
      fontWeight: 500,
      color: "#4A5568",
      letterSpacing: "-0.42px",
      py: 0,
      px: 0,
      minWidth: 0,
      minHeight: "24px",
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      lineHeight: "24px",
    },
    /**
     * MUI X date fields render section containers (not plain <input>) in many cases.
     * Center them explicitly so all schedule/date pickers share vertical alignment.
     */
    "& .MuiPickersSectionList-root, & .MuiPickersInputBase-sectionsContainer": {
      display: "flex",
      alignItems: "center",
      minHeight: "24px",
      lineHeight: "24px",
    },
    /** Tight gap between value and calendar icon. */
    "& .MuiInputAdornment-root": {
      marginInlineStart: "2px",
      marginInlineEnd: 0,
    },
    "& .MuiIconButton-root": {
      padding: "4px",
    },
    /** Adornment opens the picker; do not disable pointer events (breaks icon + some field opens). */
    "& .MuiButtonBase-root": {
      color: "rgba(0,0,0,0.45)",
      cursor: "pointer",
    },
    cursor: "pointer",
  },
};

function buildPhasePickerStartFieldSx(params: {
  alwaysShowRangeRow: boolean;
  effectiveHasStart: boolean;
}): SxProps<Theme> {
  const { alwaysShowRangeRow, effectiveHasStart } = params;
  const root = pickerInRangeFieldSx["& .MuiOutlinedInput-root"];
  return {
    ...pickerInRangeFieldSx,
    "& .MuiOutlinedInput-root": {
      ...root,
      width: "100%",
      minWidth: 0,
      ...(!alwaysShowRangeRow && !effectiveHasStart
        ? { minWidth: "min(100%, 9.5rem)" }
        : {}),
    },
  };
}

function buildPhasePickerEndFieldSx(): SxProps<Theme> {
  const root = pickerInRangeFieldSx["& .MuiOutlinedInput-root"];
  return {
    ...pickerInRangeFieldSx,
    "& .MuiOutlinedInput-root": {
      ...root,
      width: "100%",
      minWidth: 0,
    },
  };
}

/** When the open-picker icon is at `start`, tighten spacing before the date sections. */
function mergePickerFieldSxForOpenIconAtStart(sx: SxProps<Theme>): SxProps<Theme> {
  const base = sx as Record<string, unknown>;
  const root = base["& .MuiOutlinedInput-root"] as Record<string, unknown> | undefined;
  return {
    ...base,
    "& .MuiOutlinedInput-root": {
      ...root,
      "& .MuiInputAdornment-root": {
        marginInlineStart: 0,
        marginInlineEnd: "2px",
      },
    },
  };
}

function earliestDayjs(
  ...candidates: Array<Dayjs | undefined | null>
): Dayjs | undefined {
  const ok = candidates.filter((d): d is Dayjs => !!d?.isValid());
  if (!ok.length) return undefined;
  return ok.reduce((a, b) => (a.isBefore(b) ? a : b));
}

function latestDayjs(
  ...candidates: Array<Dayjs | undefined | null>
): Dayjs | undefined {
  const ok = candidates.filter((d): d is Dayjs => !!d?.isValid());
  if (!ok.length) return undefined;
  return ok.reduce((a, b) => (a.isAfter(b) ? a : b));
}

/**
 * MUI DateTimePicker ignores or mishandles clicks when `minDateTime` & `maxDateTime` are inverted
 * or equal with no selectable window — common when flight + neighbor bounds race RHF updates.
 */
function safePickerMinMaxDateTime(
  min?: Dayjs,
  max?: Dayjs
): { minDateTime?: Dayjs; maxDateTime?: Dayjs } {
  const minOk = min?.isValid() ? min : undefined;
  const maxOk = max?.isValid() ? max : undefined;
  if (!minOk && !maxOk) return {};
  if (!minOk) return { maxDateTime: maxOk };
  if (!maxOk) return { minDateTime: minOk };
  if (minOk.isAfter(maxOk)) return {};
  if (minOk.isSame(maxOk)) return {};
  return { minDateTime: minOk, maxDateTime: maxOk };
}

/** Tightens MUI min/max so phases cannot be dragged across neighbors (contiguous chain). */
export type PhaseCalendarNeighborBounds = {
  startMin?: Dayjs;
  endMax?: Dayjs;
};

export type BudgetPhaseMetaDateTimeRangeProps = {
  phaseIndex: number;
  /** When set, overrides default "Phase N …" picker aria labels (e.g. campaign run schedule). */
  ariaLabels?: { start: string; end: string };
  eventTimezone: string;
  time_start: string;
  time_end: string;
  /** When set, pickers cannot move outside the campaign run schedule (same bounds as save validation). */
  flightRangeInEventZone?: { start: Dayjs; end: Dayjs } | null;
  /**
   * When set, start cannot move before `startMin` (typically previous phase end) and end cannot
   * move after `endMax` (keeps at least one Meta interval for the following phase).
   */
  neighborPickerBounds?: PhaseCalendarNeighborBounds | null;
  /**
   * When true, start and end pickers stay visible with full-width slots even before start is set.
   * Default matches phased budget rows: one shell, then start → end after the first pick.
   */
  alwaysShowRangeRow?: boolean;
  /**
   * When true and `flightRangeInEventZone` is set, the start field is read-only and shows the
   * campaign flight start (first budget phase must align with schedule start).
   */
  lockStartToCampaignFlight?: boolean;
  /**
   * When true and `flightRangeInEventZone` is set, the end field is read-only and shows the
   * campaign flight end (last budget phase must align with schedule end).
   */
  lockEndToCampaignFlight?: boolean;
  /**
   * When true, the start picker is replaced by a read-only field (e.g. edit schedule modal when
   * Meta start is locked). End remains editable unless {@link disableEndPicker} is set.
   */
  disableStartPicker?: boolean;
  /**
   * When true, the end picker is replaced by a read-only field (e.g. full run range locked).
   */
  disableEndPicker?: boolean;
  /**
   * When true (default), values snap to Meta’s UTC 15-minute grid (phased budget segments).
   * When false, minute-level event-wall times are preserved (campaign run schedule).
   */
  snapPickerToMetaUtcQuarterHour?: boolean;
  /**
   * MUI clock minutes step. Defaults to 15 when {@link snapPickerToMetaUtcQuarterHour} is true,
   * otherwise 5 (1-minute lists are unreliable in MUI v6 desktop datetime pickers).
   */
  pickerMinutesStep?: number;
  /**
   * Minimum minutes between start and end for picker bounds. Defaults to
   * {@link META_PHASE_MIN_GAP_MINUTES} when snapping, otherwise 1.
   */
  minStartEndGapMinutes?: number;
  onChangeStart: (wall: string) => void;
  onChangeEnd: (wall: string) => void;
  /** Highlights the start or end control when validation points at that side. */
  invalidTimeField?: "start" | "end" | null;
  /**
   * When true, the calendar open icon renders before the value (MUI `inputAdornment` start).
   * Set for campaign run pickers and phased rows in {@link GuidedBudgetEditor} /
   * {@link GuidedScheduleEditor} ({@link GuidedBudgetPhaseRow}).
   */
  pickerOpenIconAtStart?: boolean;
};

/**
 * MUI `DateTimePicker` pair for phased budget rows and campaign run schedule (same shell; optional
 * {@link BudgetPhaseMetaDateTimeRangeProps.ariaLabels}). Requires a parent
 * {@link LocalizationProvider} (see `GuidedBudgetEditor`, `GuidedScheduleEditor`). Event-timezone
 * aware. By default the clock uses 15-minute steps and snaps to Meta’s UTC quarter-hour on change;
 * set {@link BudgetPhaseMetaDateTimeRangeProps.snapPickerToMetaUtcQuarterHour} to false for
 * campaign run pickers (5-minute clock steps; values stored without Meta UTC snap).
 *
 * Layout: one bordered range shell; start → end on the left. Field + calendar icon open the
 * popper. When a boundary is flight-locked (phased budget first/last row vs campaign run), that
 * side shows a read-only label (“Campaign start” / “Campaign end”) instead of a datetime picker.
 * Do not set the picker's root `readOnly` prop on pickers (it disables the open control incorrectly).
 *
 * Do **not** control the picker's `open` prop from React state: with react-hook-form `useWatch` the
 * parent re-renders often; MUI's controlled-open + internal draft can desync so day clicks appear
 * to do nothing. Let the desktop picker manage its own popper open state.
 */
export default function BudgetPhaseMetaDateTimeRange({
  phaseIndex,
  ariaLabels,
  eventTimezone,
  time_start,
  time_end,
  flightRangeInEventZone,
  neighborPickerBounds = null,
  alwaysShowRangeRow = false,
  lockStartToCampaignFlight = false,
  lockEndToCampaignFlight = false,
  disableStartPicker = false,
  disableEndPicker = false,
  snapPickerToMetaUtcQuarterHour = true,
  pickerMinutesStep: pickerMinutesStepProp,
  minStartEndGapMinutes: minStartEndGapMinutesProp,
  onChangeStart,
  onChangeEnd,
  invalidTimeField = null,
  pickerOpenIconAtStart = false,
}: BudgetPhaseMetaDateTimeRangeProps) {
  const snapMeta = snapPickerToMetaUtcQuarterHour;
  /**
   * MUI `MultiSectionDigitalClock` + `timeSteps.minutes: 1` is brittle (very large minute lists /
   * odd interaction with `closeOnSelect`). Campaign schedule uses 5-minute clock steps while
   * still persisting minute-precision wall times (no Meta UTC snap).
   */
  const pickerMinutesStep =
    pickerMinutesStepProp ?? (snapMeta ? 15 : 5);
  const minStartEndGapMinutes =
    minStartEndGapMinutesProp ??
    (snapMeta ? META_PHASE_MIN_GAP_MINUTES : 1);

  const timeStepsForPicker = useMemo(
    () =>
      snapMeta
        ? { hours: 1, minutes: pickerMinutesStep, seconds: 0 }
        : { hours: 1, minutes: pickerMinutesStep },
    [snapMeta, pickerMinutesStep]
  );

  const startAriaLabel =
    ariaLabels?.start ?? `Phase ${phaseIndex + 1} start date and time`;
  const endAriaLabel =
    ariaLabels?.end ?? `Phase ${phaseIndex + 1} end date and time`;

  const tz = eventTimezone?.trim() || "UTC";
  const hasStart = Boolean(time_start?.trim());
  const hasEnd = Boolean(time_end?.trim());

  const flightStart = flightRangeInEventZone?.start?.isValid()
    ? flightRangeInEventZone.start
    : null;
  const flightEnd = flightRangeInEventZone?.end?.isValid()
    ? flightRangeInEventZone.end
    : null;

  const startLockedToFlight = Boolean(lockStartToCampaignFlight && flightStart);
  const endLockedToFlight = Boolean(lockEndToCampaignFlight && flightEnd);

  /** Locked flight boundary counts as set for range row disclosure (arrow / end column). */
  const effectiveHasStart = hasStart || startLockedToFlight;
  const effectiveHasEnd = hasEnd || endLockedToFlight;

  const startValue: Dayjs | null = (() => {
    if (startLockedToFlight) return flightStart;
    if (!time_start?.trim()) return null;
    if (!snapMeta) return wallMinuteToDayjsInEventZone(time_start, tz);
    const snapped = snapWallMinuteToMetaBudgetUtc(time_start, tz, "start");
    return wallMinuteToDayjsInEventZone(snapped || time_start, tz);
  })();

  const endValue: Dayjs | null = (() => {
    if (endLockedToFlight) return flightEnd;
    if (!time_end?.trim()) return null;
    if (!snapMeta) return wallMinuteToDayjsInEventZone(time_end, tz);
    const snapped = snapWallMinuteToMetaBudgetUtc(time_end, tz, "end");
    return wallMinuteToDayjsInEventZone(snapped || time_end, tz);
  })();

  const minEnd = startValue?.isValid()
    ? startValue.add(minStartEndGapMinutes, "minute")
    : undefined;
  const maxStart = endValue?.isValid()
    ? endValue.subtract(minStartEndGapMinutes, "minute")
    : undefined;

  const flightMaxStart = flightRangeInEventZone?.end?.isValid()
    ? flightRangeInEventZone.end.subtract(minStartEndGapMinutes, "minute")
    : undefined;

  const startMin = latestDayjs(
    flightRangeInEventZone?.start?.isValid()
      ? flightRangeInEventZone.start
      : undefined,
    neighborPickerBounds?.startMin?.isValid()
      ? neighborPickerBounds.startMin
      : undefined
  );
  const startMax = earliestDayjs(maxStart, flightMaxStart);

  const endMin = latestDayjs(
    minEnd,
    flightRangeInEventZone?.start?.isValid()
      ? flightRangeInEventZone.start
      : undefined
  );
  const endMax = earliestDayjs(
    flightRangeInEventZone?.end?.isValid()
      ? flightRangeInEventZone.end
      : undefined,
    neighborPickerBounds?.endMax?.isValid()
      ? neighborPickerBounds.endMax
      : undefined
  );

  const startMinMax = safePickerMinMaxDateTime(startMin, startMax);
  const endMinMax = safePickerMinMaxDateTime(endMin, endMax);

  const readOnlyPickerSlotProps = {
    openPickerButton: {
      tabIndex: -1,
      disableRipple: true,
      "aria-hidden": true,
    },
  } as const;

  const showArrow =
    alwaysShowRangeRow || (effectiveHasStart && effectiveHasEnd);
  const showEndPicker = alwaysShowRangeRow || effectiveHasStart;
  const startPlaceholder = alwaysShowRangeRow
    ? "Start"
    : !effectiveHasStart
      ? "Select date range"
      : "Start";

  /** Share row width on `sm+`; stack full-width on narrow viewports when start + end both show. */
  const pickerSlotFlex = showEndPicker
    ? { base: "1 1 100%", sm: 1 }
    : 1;

  const startFieldSxBase = buildPhasePickerStartFieldSx({
    alwaysShowRangeRow,
    effectiveHasStart,
  });
  const endFieldSxBase = buildPhasePickerEndFieldSx();
  const startFieldSx = pickerOpenIconAtStart
    ? mergePickerFieldSxForOpenIconAtStart(startFieldSxBase)
    : startFieldSxBase;
  const endFieldSx = pickerOpenIconAtStart
    ? mergePickerFieldSxForOpenIconAtStart(endFieldSxBase)
    : endFieldSxBase;

  const pickerSlotPropsForOpenIconPosition = pickerOpenIconAtStart
    ? ({ inputAdornment: { position: "start" as const } } as const)
    : {};

  const invalidOutlineBoxProps =
    (side: "start" | "end") =>
      invalidTimeField === side
        ? {
            outline: "2px solid",
            outlineColor: "red.400",
            outlineOffset: "2px",
            borderRadius: "md",
          }
        : {};

  return (
    <Box
      w="full"
      minW={0}
      display="flex"
      alignItems="center"
      borderWidth="1px"
      borderColor="#E2E2E2"
      borderRadius="md"
      bg="white"
      boxShadow="0px 2px 6px rgba(0, 0, 0, 0.05)"
      px={3}
      py={2.5}
    >
      <Flex w="full" align="center" justify="flex-start" gap={2} minH="28px">
        <Flex
          minW={0}
          w="full"
          align="center"
          justify="flex-start"
          gap={2}
          flexWrap={{ base: "wrap", sm: "nowrap" }}
          rowGap={2}
        >
          <Box
            flex={pickerSlotFlex}
            minW={0}
            maxW="100%"
            w="full"
            {...invalidOutlineBoxProps("start")}
          >
            {startLockedToFlight ? (
              <TextField
                size="small"
                fullWidth
                value="Campaign start"
                aria-label={startAriaLabel}
                InputProps={{ readOnly: true }}
                sx={{
                  ...startFieldSx,
                  "& .MuiOutlinedInput-root .MuiInputBase-input": {
                    cursor: "default",
                    color: "rgba(74, 85, 104, 0.85)",
                  },
                }}
              />
            ) : disableStartPicker ? (
              <TextField
                size="small"
                fullWidth
                value={
                  startValue?.isValid()
                    ? startValue.format(PHASE_DATETIME_FIELD_FORMAT)
                    : time_start || ""
                }
                aria-label={startAriaLabel}
                InputProps={{ readOnly: true }}
                sx={{
                  ...startFieldSx,
                  "& .MuiOutlinedInput-root .MuiInputBase-input": {
                    cursor: "default",
                    color: "rgba(74, 85, 104, 0.85)",
                  },
                }}
              />
            ) : (
              <DateTimePicker
                label=""
                timezone={tz}
                value={startValue}
                format={PHASE_DATETIME_FIELD_FORMAT}
                formatDensity="spacious"
                minDateTime={startMinMax.minDateTime}
                maxDateTime={startMinMax.maxDateTime}
                ampm
                timeSteps={timeStepsForPicker}
                closeOnSelect={snapMeta}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: startPlaceholder,
                    "aria-label": startAriaLabel,
                    sx: startFieldSx,
                  },
                  actionBar: { actions: ["clear", "accept"] },
                  ...readOnlyPickerSlotProps,
                  popper: pickerPopperSlotProps,
                  desktopTrapFocus: pickerDesktopTrapFocusSlotProps,
                  ...pickerSlotPropsForOpenIconPosition,
                }}
                onChange={(d) =>
                  onChangeStart(
                    snapMeta
                      ? snapPickerDayjsToWallMinute(d, tz, "start")
                      : formatPickerDayjsToEventWallMinute(d, tz)
                  )
                }
              />
            )}
          </Box>
          {showArrow ? (
            <Text
              fontSize="sm"
              fontWeight="normal"
              color="gray.400"
              flexShrink={0}
              lineHeight="23px"
              letterSpacing="-0.42px"
              alignSelf="center"
              px={{ base: 0.5, sm: 0 }}
            >
              →
            </Text>
          ) : null}
          {showEndPicker ? (
            <Box
              flex={pickerSlotFlex}
              minW={0}
              maxW="100%"
              w="full"
              {...invalidOutlineBoxProps("end")}
            >
              {endLockedToFlight ? (
                <TextField
                  size="small"
                  fullWidth
                  value="Campaign end"
                  aria-label={endAriaLabel}
                  InputProps={{ readOnly: true }}
                  sx={{
                    ...endFieldSx,
                    "& .MuiOutlinedInput-root .MuiInputBase-input": {
                      cursor: "default",
                      color: "rgba(74, 85, 104, 0.85)",
                    },
                  }}
                />
              ) : disableEndPicker ? (
                <TextField
                  size="small"
                  fullWidth
                  value={
                    endValue?.isValid()
                      ? endValue.format(PHASE_DATETIME_FIELD_FORMAT)
                      : time_end || ""
                  }
                  aria-label={endAriaLabel}
                  InputProps={{ readOnly: true }}
                  sx={{
                    ...endFieldSx,
                    "& .MuiOutlinedInput-root .MuiInputBase-input": {
                      cursor: "default",
                      color: "rgba(74, 85, 104, 0.85)",
                    },
                  }}
                />
              ) : (
                <DateTimePicker
                  label=""
                  timezone={tz}
                  value={endValue}
                  format={PHASE_DATETIME_FIELD_FORMAT}
                  formatDensity="spacious"
                  minDateTime={endMinMax.minDateTime}
                  maxDateTime={endMinMax.maxDateTime}
                  ampm
                  timeSteps={timeStepsForPicker}
                  closeOnSelect={snapMeta}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      placeholder: "End",
                      "aria-label": endAriaLabel,
                      sx: endFieldSx,
                    },
                    actionBar: { actions: ["clear", "accept"] },
                    ...readOnlyPickerSlotProps,
                    popper: pickerPopperSlotProps,
                    desktopTrapFocus: pickerDesktopTrapFocusSlotProps,
                    ...pickerSlotPropsForOpenIconPosition,
                  }}
                  onChange={(d) =>
                    onChangeEnd(
                      snapMeta
                        ? snapPickerDayjsToWallMinute(d, tz, "end")
                        : formatPickerDayjsToEventWallMinute(d, tz)
                    )
                  }
                />
              )}
            </Box>
          ) : null}
        </Flex>
      </Flex>
    </Box>
  );
}
